import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchSessions, fetchBanners } from '../api/endpoints';
import SessionCard from '../components/SessionCard';
import BannerCarousel from '../components/BannerCarousel';
import Splash from '../components/Splash';
import { useUserStore } from '../store/userStore';
import i18n from '../i18n';

export default function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isAdmin = useUserStore((s) => s.isAdmin);
  const lang = useUserStore((s) => s.lang);
  const tgId = useUserStore((s) => s.tgId);
  const adminsLoaded = useUserStore((s) => s.adminsLoaded);
  const activeOrderId = useUserStore((s) => s.activeOrderId);

  const { data: sessions, isLoading } = useQuery({ queryKey: ['sessions'], queryFn: fetchSessions });
  const { data: banners = [] } = useQuery({ queryKey: ['banners'], queryFn: fetchBanners });

  if (isLoading) return <Splash />;

  return (
    <div className="min-h-screen pb-8">
      <div className="flex items-center justify-between px-4 pt-4">
        <h1 className="text-2xl font-bold">{t('home.title')}</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              const next = lang === 'ru' ? 'uz' : 'ru';
              localStorage.setItem('lang', next);
              i18n.changeLanguage(next);
              useUserStore.setState({ lang: next });
            }}
            className="px-3 py-1.5 rounded-full bg-bg-elev text-sm uppercase font-semibold"
          >
            {lang}
          </button>
          <button
            onClick={() => navigate('/my-tickets')}
            className="w-9 h-9 rounded-full bg-bg-elev grid place-items-center"
            aria-label={t('common.myTickets')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M3 8a2 2 0 012-2h14a2 2 0 012 2v2a2 2 0 100 4v2a2 2 0 01-2 2H5a2 2 0 01-2-2v-2a2 2 0 100-4V8z" stroke="#fff" strokeWidth="1.6"/>
            </svg>
          </button>
          {isAdmin && (
            <button
              onClick={() => navigate('/admin')}
              className="px-4 py-2 rounded-full bg-accent text-base font-bold shadow-lg"
            >
              ⚙ {t('common.admin')}
            </button>
          )}
        </div>
      </div>

      {/* Tiny diagnostic line: own tg_id + how many admin ids the api parsed.
          Lets the operator see what's going on when the admin button is missing. */}
      {tgId != null && (
        <div className="px-4 text-xs text-muted mt-1">
          id: {tgId}
          {typeof adminsLoaded === 'number' && ` · admins_loaded: ${adminsLoaded}`}
        </div>
      )}

      {activeOrderId != null && (
        <button
          onClick={() => navigate(`/payment/${activeOrderId}`)}
          className="mx-4 mt-3 w-[calc(100%-2rem)] flex items-center justify-between gap-3 px-4 py-3 rounded-2xl bg-warn/15 border border-warn/40 active:bg-warn/25"
        >
          <div className="text-left">
            <div className="text-sm font-semibold text-warn">{t('home.heldBanner.title')}</div>
            <div className="text-xs text-muted mt-0.5">{t('home.heldBanner.subtitle')}</div>
          </div>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M9 6l6 6-6 6" stroke="#f59e0b" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}

      <BannerCarousel banners={banners} />

      <div className="px-4 mt-5 grid grid-cols-2 gap-3">
        {sessions?.length ? (
          sessions.map((s) => (
            <SessionCard key={s.id} session={s} onClick={() => navigate(`/session/${s.id}`)} />
          ))
        ) : (
          <div className="col-span-2 text-muted text-center py-12">{t('home.empty')}</div>
        )}
      </div>
    </div>
  );
}
